import { CustomersAndSuppliers } from 'components/VatChecker/CustomersAndSuppliers';
import { ViesLogs } from 'components/VatChecker/CustomersAndSuppliers/vies-logs';
import { DownloadCertificate } from 'components/VatChecker/download-Certificate';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnModalOff,
  turnModalOn,
  turnMessageOn,
} from 'store/app/actions';
import {
  AdvancedUserRightsCodes,
  DrawerType,
  WindowType,
} from 'store/app/enums';
import { Organisation } from 'store/organisation/models';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import {
  checkContactRequest,
  checkContactsRequest,
  fetchContactRequest,
  fetchViesContactsRequest,
  hideContactRequest,
  refreshContactRequest,
  refreshContactsRequest,
} from 'store/vat-checker/actions';
import { Contact, HideContactData } from 'store/vat-checker/models';
import {
  getViesChecks,
  getViesContacts,
  getViesInfos,
  getViesLoading,
} from 'store/vat-checker/selectors';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import GenTaxImage from 'assets/images/gen-tax-img.svg';
import {
  Preference,
  PreferenceActions,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';
import { getAuthUser } from 'store/auth/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import routes from 'routes';
import { AccountType } from 'store/api/enums';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { getBillingHasAdvancedRights } from 'store/billing/selectors';
import { useTranslation } from 'react-i18next';
import { MessageStates } from 'containers/MessageBox';

export const FILTER_BOX_SELECTED = 'filter-box-selected';

export const CustomersSuppliersPage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const viesInfos = useSelector(getViesInfos);
  const currentUser = useSelector(getAuthUser);
  const viesLogs = useSelector(getViesChecks);
  const contacts = useSelector(getViesContacts);
  const orgs = useSelector(getOrgOrganisations);
  const loading = useSelector(getViesLoading);
  const hasAdvancedUserRights = useSelector(getBillingHasAdvancedRights);
  const [selectedOrg, setSelectedOrg] = React.useState<Organisation>();
  const [gettingLogs, setGettingLogs] = React.useState<string>();
  const [showPage, setShowPage] = React.useState(false);
  const params: any = useParams();
  const conId = params.connectionId;

  const closeDrawer = () => dispatch(turnDrawerOff());
  const preferences = usePreferences();
  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        PreferenceSections.VIES,
        preference,
        currentUser?.userUuid
      );
    return false;
  };

  const hideContact = (hideContactData: HideContactData) => {
    if (selectedOrg) {
      dispatch(hideContactRequest(hideContactData, selectedOrg?.connectionId));
    }
  };

  React.useEffect(() => {
    setShowPage(true);
    currentUser &&
      checkPageLoaded(location.pathname, routes.vies.qualifiedEntities);
  }, []);
  React.useEffect(() => {
    if (conId && orgs && orgs.length > 0) {
      dispatch(fetchViesContactsRequest(conId));
      const currentOrg = orgs?.find((org) => org.connectionId == conId);
      setSelectedOrg(currentOrg);
    }
  }, [orgs]);

  React.useEffect(() => {
    if (gettingLogs && viesLogs) {
      const currentContact = contacts?.find(
        (contact) => contact.name == gettingLogs
      );
      const drawerData: DrawerData = {
        title: gettingLogs,
        type: DrawerType.VIES_LOGS,
        component: (
          <ViesLogs
            viesLogs={viesLogs}
            currentContact={currentContact}
            openDownloadCertificateModal={openDownloadCertificateModal}
            providerName={selectedOrg?.providerName}
            closeDrawer={closeDrawer}
          />
        ),
      };

      dispatch(turnDrawerOn(drawerData));
      setGettingLogs(undefined);
    }
  }, [viesLogs]);

  React.useEffect(() => {
    if (!currentUser || !orgs || !conId) return;
    const selectedOrganisation = orgs.find((o) => o.connectionId === conId);
    const isStaff = AccountType[currentUser.accountType] === AccountType.STAFF;
    const hasRight =
      !hasAdvancedUserRights ||
      hasAdvancedUserRight(
        currentUser,
        selectedOrganisation as any,
        AdvancedUserRightsCodes.VAT_NUMBER_CHECKER
      );
    if (isStaff && !hasRight) {
      const message: MessageData = {
        title: t('common.attention'),
        description: t('global.use-vat-checker-rights-warning'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      navigate(routes.main, { replace: true });
    }
  }, [currentUser, orgs, conId, hasAdvancedUserRights]);

  const openDownloadCertificateModal = (contact: Contact, invoice?: string) => {
    const currentContact = contact
      ? contact
      : contacts?.find((contact) => contact.name == gettingLogs);
    const modalData: ModalData = {
      title: '',
      type: WindowType.DownloadCertificate,
      component: (
        <DownloadCertificate
          contact={currentContact}
          invoice={invoice ? invoice : ''}
          onClose={() => turnModalOff()}
        />
      ),
    };
    dispatch(turnModalOn(modalData));
  };

  const openConfirmWindow = () => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('vat-checker.check-vat-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={checkContacts}
          param={conId}
          showCancelBtn={true}
          okText={t('common.proceed')}
          description={t('vat-checker.check-vat-all-description')}
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const checkContacts = () => dispatch(checkContactsRequest(conId));

  const checkContact = (contactId) =>
    dispatch(checkContactRequest(contactId, conId));

  const getHistoryLogs = (contact: Contact) => {
    if (contact?.contactUuid) {
      setGettingLogs(contact?.name);
      dispatch(fetchContactRequest(conId, contact?.contactUuid));
    } else alert(t('organisation.missing-connection-id'));
  };

  const onRefreshContacts = (connectionId: string) => {
    dispatch(refreshContactsRequest(connectionId));
  };

  const onRefreshContact = (contactId: string, connectionId: string) => {
    dispatch(refreshContactRequest(contactId, connectionId));
  };

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${t('vat-checker.customers-and-suppliers-page-title')} | ${process.env.PROJECT_NAME}`}</title>
            <meta
              name="description"
              content={t('vat-checker.page-meta-description')}
            />
          </Helmet>

          <CustomersAndSuppliers
            loading={loading}
            contacts={contacts}
            organisation={selectedOrg}
            openDownloadCertificateModal={openDownloadCertificateModal}
            openConfirmWindow={openConfirmWindow}
            checkContact={checkContact}
            getHistoryLogs={getHistoryLogs}
            viesInfo={viesInfos.find((vi) => vi.connectionId === conId)}
            onRefreshContacts={onRefreshContacts}
            onRefreshContact={onRefreshContact}
            handlePreferences={handlePreferences}
            hideContact={hideContact}
          />
        </>
      )}
    </React.Fragment>
  );
};
